<template>
  <div id="rg-object-history">
    <to-live-map-button :isMobileDevice="isMobileDevice"></to-live-map-button>
    <rg-video-play-back-button
      v-if="hasRadarGridCameras"
      :handleClick="toggleIsVideoPlayBack"
      :disabled="isAvailableVideoPlayback && isVideoPlayBackShow"
    ></rg-video-play-back-button>
    <rg-video-play-back
      ref="rgVideoPlayBack"
      v-if="isVideoPlayBackShow"
      :cameras="radarGridCameras"
      :playbackCameraId="playbackCameraId"
      :handleClickCloseButton="toggleIsVideoPlayBack"
      :start="playbackStartTime"
      :current="playbackTimeNow"
      :end="playbackEndTime"
      :fps="videoFps"
      :isPause="!isPlayBackStatusPlay"
      :handleClickCameraPlaybackListItem="setPlaybackCameraId"
    ></rg-video-play-back>
    <rg-history-setting-button
      :isMobileDevice="isMobileDevice"
    ></rg-history-setting-button>
    <div :id="mapId" class="rg-object-history-map"></div>
    <rg-date-input-dialog
      @sendInputValue="setUpPlayback($event)"
      :active="isActiveDateInputDialog"
    />
    <rg-playback-controller></rg-playback-controller>
    <div class="rg-detail-info-area" v-show="layerFilters.isRgObjectVisibility">
      <rg-deselect-all-button
        v-if="hasVisibleTrackRgObjects"
        @deselect-all="deSelectAll"
      ></rg-deselect-all-button>
      <rg-object-detail-list
        :rgObjectList="selectedRgObjectList"
      ></rg-object-detail-list>
      <rg-zoom-pic
        v-for="expandImgObject in expandImgRgObjects"
        :key="expandImgObject.id"
        v-bind="expandImgObject"
        @onClickCloseBtn="handleCloseExpandImg(expandImgObject.id)"
      ></rg-zoom-pic>
    </div>
  </div>
</template>

<script>
import "mapbox-gl/dist/mapbox-gl.css";
import Mapbox from "mapbox-gl";
import RgZoomPic from "./components/RgZoomPic.vue";
import RgHistorySettingButton from "./components/RgHistorySettingButton";
import ToLiveMapButton from "./components/ToLiveMapButton";
import RgVideoPlayBackButton from "./components/RgVideoPlayBackButton";
import RgPlaybackController from "./components/RgPlaybackController";
import RgDateInputDialog from "./components/RgDateInputDialog.vue";
import RgVideoPlayBack from "./components/RgVideoPlayBack";
import RgDeselectAllButton from "./components/RgDeselectAllButton";
import RgObjectDetailList from "./components/RgObjectDetailList";
import { MAP_STYLE_DEFAULT } from "@/config/const";
import { createNamespacedHelpers } from "vuex";
import C from "./Constants";
import * as RGUtil from "@/utils/RadarGridObjectUtil";
import moment from "moment-timezone";
import * as turf from "@turf/turf";
import { PrivatePlotMapSource, PrivatePlotMapLayer } from "@/config/Map";
import mapKeys from "lodash/mapKeys";
import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();
// https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-font
const DEFAULT_RG_TEXT_FONTS = ["Open Sans Regular", "Arial Unicode MS Regular"];
const BOLD_RG_TEXT_FONTS = ["Open Sans Bold", "Arial Unicode MS Bold"];
const DEFAULT_RG_TEXT_COLOR = AISEA_COLOR.WHITE;

const {
  mapActions: mapActionsOfRGPlayback,
  mapGetters: mapGettersOfRGPlayback,
  mapMutations: mapMutationsOfRGPlayback,
} = createNamespacedHelpers("radarGridPlayback");

export default {
  name: "RgObjectHistoryMap",
  components: {
    ToLiveMapButton,
    RgHistorySettingButton,
    RgPlaybackController,
    RgDateInputDialog,
    RgVideoPlayBackButton,
    RgVideoPlayBack,
    RgDeselectAllButton,
    RgObjectDetailList,
    RgZoomPic,
  },
  props: {
    isMobileDevice: {
      type: Boolean,
      default: false,
    },
    // mapbox container id
    mapId: {
      type: String,
      required: true,
    },
    // mapbox initialize setting
    mapSetting: {
      type: Object,
      required: true,
    },
    mapStyle: {
      type: String,
      default: MAP_STYLE_DEFAULT,
    },
    updateMapStyle: {
      type: Function,
      required: true,
    },
    mapLocale: {
      type: String,
      default: null,
    },
    textLayerIds: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    showPrivatePlot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      map: null,
      isStyleReady: false,
      requestAnimationId: null,
      isVideoPlayBackShow: false,
      playbackCameraId: "",
      selectedRgObjects: {
        // objectId(string): {geometry: Object; id: string; properties: Object; type: "Feature";}
      }, // RgObjectのFeatureのマップ
      tmpSelectedRgObjects: {}, // 一時的に選択されたRgObjectを保持する
    };
  },
  watch: {
    mapStyle(style) {
      if (this.isMapReady) {
        this.map.setStyle(style);
      }
    },
    layerFilters(v) {
      this.setLayerFilters(v);
    },
    privatePlotFeatures: {
      handler() {
        this.updatePrivatePlotLayer();
      },
      deep: true,
    },
    radarGridCameraMasterFeatures: {
      handler() {
        this.updateRadarCameraMasterLayer();
      },
      deep: true,
    },
    radarGridCameras(val) {
      if (!Array.isArray(val)) return;
      if (val.length === 0) return;
      this.playbackCameraId = val[0].id || "";
    },
    playbackCameraId() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
    },
    playbackStatus() {
      this.animate();
    },
    isVideoPlayBackShow() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
    },
    visibleTrackVessels() {
      this.animate();
    },
    visibleTrackRgObjects() {
      const { RG_OBJECT_CIRCLE_LAYER, RG_OBJECT_SYMBOL_LAYER } = C.LAYER_ID;
      if (this.map.getLayer(RG_OBJECT_CIRCLE_LAYER)) {
        this.map.setFilter(
          RG_OBJECT_CIRCLE_LAYER,
          this.rgCircleFilterCondition,
        );
      }
      if (this.map.getLayer(RG_OBJECT_SYMBOL_LAYER)) {
        this.map.setLayoutProperty(
          RG_OBJECT_SYMBOL_LAYER,
          "text-font",
          this.rgFontText,
        );
        this.map.setLayoutProperty(
          RG_OBJECT_SYMBOL_LAYER,
          "text-size",
          this.rgFontSize,
        );
        this.map.setPaintProperty(
          RG_OBJECT_SYMBOL_LAYER,
          "text-color",
          this.rgTextColor,
        );
      }
      this.animate();
    },
    async playbackTimeNow() {
      if (this.isPlayBackStatusPlay) return;
      this.animate();
    },
  },
  computed: {
    selectedRgObjectList() {
      const { visibleTrackRgObjects, selectedRgObjects, tmpSelectedRgObjects } =
        this;
      return visibleTrackRgObjects.map((id) => {
        const selectedRgObject = selectedRgObjects[id] ||
          tmpSelectedRgObjects[id] || { id }; // レンダリングタイミングの関係でselectedRgObjects[id]がundefinedになる場合のfallback // 選択状態の解除をできるように、idは必ず持たせる
        return selectedRgObject;
      }); // SelectedRgObject[]
    },
    rgCircleFilterCondition() {
      return [
        "all",
        ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
        ["any", ...this.visibleTrackRgObjects.map((id) => ["==", "id", id])],
      ];
      // [
      //   "all",
      //   ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
      //   [
      //     "any",
      //     ["==", "id", string][]
      //   ]
      // ]
    },
    rgTextColor() {
      return [
        "case",
        [
          "any", // どれかに合致する場合
          ...this.visibleTrackRgObjects.map((id) => ["==", ["get", "id"], id]),
        ],
        ["get", "textColor"],
        DEFAULT_RG_TEXT_COLOR, // default
      ];
      // ["case",
      //   ["any", ["==", ["get", "id"], string]][],
      //   ["get", "textColor"],,
      //   "#F2F2F2"
      // ]
    },
    rgFontSize() {
      return [
        "case",
        [
          "any", // どれかに合致する場合
          ...this.visibleTrackRgObjects.map((id) => ["==", ["get", "id"], id]),
        ],
        18, // 選択中の場合
        14, // default
      ];
      // ["case",
      //   ["any", ["==", ["get", "id"], string]][],
      //   18,
      //   14
      // ]
    },
    rgFontText() {
      return [
        "case",
        [
          "any", // どれかに合致する場合
          ...this.visibleTrackRgObjects.map((id) => ["==", ["get", "id"], id]),
        ],
        ["literal", BOLD_RG_TEXT_FONTS], // 選択中の場合
        ["literal", DEFAULT_RG_TEXT_FONTS], // default
      ];
      // ["case",
      //   ["any", ["==", ["get", "id"], string]][],
      //   ["literal", BOLD_RG_TEXT_FONTS],
      //   ["literal", DEFAULT_RG_TEXT_FONTS]
      // ]
    },
    ...mapGettersOfRGPlayback([
      "isInitial",
      "playbackBaseTime",
      "playbackTimeNow",
      "playbackStartTime",
      "playbackEndTime",
      "playbackStatus",
      "playbackSecondsPerFrame",
      "layerFilters",
      "isVisibleTrack",
      "visibleTracks",
      "privatePlotFeatures",
      "radarGridCameraMaster",
      "cameraHashMap",
      "radarGridCameraMasterFeatures",
      "visibleTrackRgObjects",
      "hasVisibleTrackRgObjects",
      "visibleTrackVessels",
      "radarGridCameras",
      "hasRadarGridCameras",
      "isActiveDateInputDialog",
      "expandImgRgObjects",
    ]),

    isMapReady() {
      return this.map instanceof Mapbox.Map;
    },

    isAvailableVideoPlayback() {
      return !this.isInitial && this.hasRadarGridCameras;
    },

    videoFps() {
      return Math.max(0.1, this.playbackSecondsPerFrame * 3);
    },

    isPlayBackStatusPlay() {
      return this.playbackStatus === C.PLAYBACK_STATUS.PLAY;
    },

    isPlayBackStatusPause() {
      return this.playbackStatus === C.PLAYBACK_STATUS.PAUSE;
    },
  },
  methods: {
    handleCloseExpandImg(objectId) {
      this.filterExpandImgRgObject({ id: objectId });
    },
    ...mapActionsOfRGPlayback([
      "getAisVesselsHistory",
      "generateCurrentGeoJson",
      "setRgPlaybackPrivatePlots",
      "setRadarCameraData",
      "resetVisibleTracksRgObject",
      "resetVisibleTracks",
      "resetState",
      "addVisibleTracksRgObject",
      "removeVisibleTracksRgObject",
      "filterExpandImgRgObject",
      "resetExpandImgRgObject",
    ]),
    ...mapMutationsOfRGPlayback([
      "SET_IS_INITIAL",
      "SET_PLAYBACK_SETTINGS",
      "SET_PLAYBACK_TIME_NOW",
      "SET_VISIBLE_TRACKS",
      "SET_RG_PLAYBACK_STATUS",
      "SET_IS_ACTIVE_DATE_INPUT_DIALOG",
    ]),

    setUp() {
      this.map = this.initMap();
      this.SET_IS_ACTIVE_DATE_INPUT_DIALOG(true); // 画面初期化時に日時入力ダイアログを表示
    },

    cleanup() {
      window.cancelAnimationFrame(this.requestAnimationId);
      this.resetState();
    },

    initMap() {
      const { mapId, mapSetting, mapStyleLoad, mapLoad } = this;
      Mapbox.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
      const map = new Mapbox.Map({
        container: mapId,
        ...mapSetting,
        style: this.mapStyle,
      });

      map.addControl(
        new Mapbox.ScaleControl({
          maxWidth: 80,
          unit: "metric",
        }),
      );
      map.addControl(new Mapbox.NavigationControl(), "bottom-left");
      map.on("style.load", mapStyleLoad);
      map.on("load", mapLoad);
      return map;
    },

    mapLoad() {
      this.map.resize();
    },

    async mapStyleLoad(_a, _b) {
      if (!this.map) return;
      this.isStyleReady = false;
      if (this.mapStyle === MAP_STYLE_DEFAULT) {
        this.textLayerIds.forEach((id) => {
          this.map.setLayoutProperty(id, "text-field", ["get", this.mapLocale]);
        });
      }
      this.isStyleReady = true;
    },

    async setUpPlayback(inputDateTime) {
      this.$vs.loading({
        container: "#rg-object-history",
        scale: 0.6,
      });

      this.isVideoPlayBackShow = false;

      let mBaseTime = moment(inputDateTime).utc();
      let mStartTime = moment(inputDateTime).utc().subtract(1, "hour");
      let mEndTime = moment(inputDateTime).utc().add(1, "hour");

      this.SET_PLAYBACK_SETTINGS({
        baseTime: mBaseTime.unix(),
        startTime: mStartTime.unix(),
        endTime: mEndTime.unix(),
        now: mStartTime.unix(),
        secondsPerFrame: 1,
        playbackStatus: C.PLAYBACK_STATUS.PAUSE,
      });

      this.resetVisibleTracks();
      this.deSelectAll(); // 詳細パネルの選択解除
      this.resetExpandImgRgObject(); // 拡大画像をリセット

      // プライベートプロット
      if (this.showPrivatePlot) {
        this.addSourcePrivatePlotLayer();
        this.addPrivatePlotLayers();
        this.setRgPlaybackPrivatePlots({
          historyStartDate: moment(mStartTime).toDate(),
          historyEndDate: moment(mEndTime).toDate(),
        });
      }

      let payload = {
        startTime: mStartTime.format("YYYY-MM-DDTHH:mm:ss") + ".000Z",
        endTime: mEndTime.format("YYYY-MM-DDTHH:mm:ss") + ".000Z",
        coordinates: C.PLAYBACK_AREA_COORDINATES,
      };

      await this.getAisVesselsHistory(payload);

      if (this.isInitial) {
        await this.setLayers();
        this.setLayerClickEvents();
        this.setMousePointer();
        this.setMapLayerPopup();
        RGUtil.addFrameIcons(this.map);
        this.setUpRadarGridCameraMasterLayer();
        this.SET_IS_INITIAL(false);
      } else {
        this.SET_VISIBLE_TRACKS({
          vessels: [],
          rgObject: [],
        });
      }

      this.SET_PLAYBACK_TIME_NOW(this.playbackBaseTime);

      this.$vs.loading.close("#rg-object-history > .con-vs-loading");

      await this.animate();
    },

    requestAnimation(e) {
      if (e.dataType === "source") {
        this.map
          .getSource(C.SOURCE_ID.RG_OBJECT_POINTS)
          .off("data", this.requestAnimation);
        this.requestAnimationId = window.requestAnimationFrame(this.animate);
      }
    },

    async animate() {
      if (this.playbackStatus === C.PLAYBACK_STATUS.PLAY) {
        // カメラを起動しているときは、カメラの描画に合わせて更新する
        if (
          this.hasHlsVideoPlayerInitialized() &&
          !this.hasHlsVideoPlayerVideoError()
        ) {
          this.SET_PLAYBACK_TIME_NOW(
            this.getHlsVideoPlayer().calculatePlayBackTimeNowByPlayer(),
          );
        } else {
          this.SET_PLAYBACK_TIME_NOW(
            this.playbackTimeNow + this.playbackSecondsPerFrame,
          );
        }
        if (this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS)) {
          this.map
            .getSource(C.SOURCE_ID.RG_OBJECT_POINTS)
            .on("data", this.requestAnimation);
        }
      }
      if (!this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS)) return;
      if (!this.map.getSource(C.SOURCE_ID.RG_OBJECT_TRACKS)) return;
      const { points = {}, tracks } = await this.generateCurrentGeoJson();
      this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS).setData(points);
      this.map.getSource(C.SOURCE_ID.RG_OBJECT_TRACKS).setData(tracks);

      // オブジェクトの詳細情報を更新
      const currentVisibleTrackRgObjectFeatures =
        this.lookupVisibleTrackRgObjectFeatures(points.features);

      const currentSelectedRgObjects = mapKeys(
        currentVisibleTrackRgObjectFeatures,
        "id",
      );
      this.selectedRgObjects = currentSelectedRgObjects;
      this.tmpSelectedRgObjects = {}; // 一時的に保持した選択オブジェクトをリセット
    },

    getHlsVideoPlayer() {
      const { rgVideoPlayBack } = this.$refs;
      if (!rgVideoPlayBack) return null;
      if (!rgVideoPlayBack.$refs.hlsVideoPlayer) return null;
      return rgVideoPlayBack.$refs.hlsVideoPlayer;
    },

    hasHlsVideoPlayerVideoError() {
      const hlsVideoPlayer = this.getHlsVideoPlayer();
      return (hlsVideoPlayer && hlsVideoPlayer.hasVideoError) === true;
    },

    hasHlsVideoPlayerInitialized() {
      const hlsVideoPlayer = this.getHlsVideoPlayer();
      return (hlsVideoPlayer && hlsVideoPlayer.initialized) === true;
    },

    setPlaybackCameraId(id) {
      this.playbackCameraId = id;
    },

    toggleIsVideoPlayBack() {
      this.isVideoPlayBackShow = !this.isVideoPlayBackShow;
    },

    lookupVisibleTrackRgObjectFeatures(
      pointFeatures, // Array<Feature>
    ) {
      if (!Array.isArray(pointFeatures)) return [];
      if (!Array.isArray(this.visibleTrackRgObjects)) return [];
      if (this.visibleTrackRgObjects.length === 0) return [];
      const visibleTrackRgObjectIds = new Set(this.visibleTrackRgObjects);
      return pointFeatures
        .filter((feature = {}) => feature.properties)
        .filter(({ properties }) => visibleTrackRgObjectIds.has(properties.id))
        .map((feature) => ({
          ...feature,
          id: feature.properties.id,
          geometry: feature.geometry,
        }));
    },

    async setLayers() {
      // 表示エリアレイヤー
      let displayArea = turf.polygon([C.PLAYBACK_AREA_COORDINATES]);
      this.map.addSource("rg-object-display-area", {
        type: "geojson",
        data: displayArea,
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_DISPLAY_AREA_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA,
        layout: {},
        paint: {
          "line-color": "#F616E0",
          "line-width": 2,
          "line-dasharray": [1, 1],
        },
      });

      //現在のGeoJson
      let currentGeoJson = await this.generateCurrentGeoJson();

      //航跡GeoJson
      this.map.addSource(C.SOURCE_ID.RG_OBJECT_TRACKS, {
        type: "geojson",
        data: currentGeoJson.tracks,
      });

      //航跡レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_HISTORY_TRACK_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1],
        },
        filter: [
          "all",
          ["==", "visible", true],
          ["!=", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT], // NOTE: 不審船は別のレイヤで表示
        ],
      });

      // 航跡のマウスイベント用レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-width": 20.0,
          "line-opacity": 0.0,
        },
        filter: [
          "all",
          ["==", "visible", true],
          ["!=", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
        ],
      });

      //位置情報のGeoJson
      this.map.addSource(C.SOURCE_ID.RG_OBJECT_POINTS, {
        type: "geojson",
        data: currentGeoJson.points,
      });

      // AIS船レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-optional": true,
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading",
          },
        },
        filter: [
          "any",
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_POLYGON_LAYER,
        type: "fill",
        minzoom: 14,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
        filter: [
          "all",
          ["==", "hasSize", true],
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "opacity",
          },
        },
        filter: [
          "all",
          ["==", "hasSize", false],
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_TEXT_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        maxzoom: 24,
        minzoom: 10,
        layout: {
          "text-field": ["get", "vesselName"],
          "text-anchor": "top",
          "text-size": 14,
          "text-max-width": 30,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
          "text-allow-overlap": false,
          "text-ignore-placement": false,
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
        filter: [
          "any",
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
        ],
      });

      // 自船マーカー
      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_MARKER_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{marker}",
          "icon-offset": [0, -15],
        },
        filter: ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
      });

      // 不審船レイヤー
      this.addRgObjectLayers();
    },

    setLayerFilters(v) {
      // 自船と他船
      if (v.isMyVesselVisibility && v.isOtherVesselVisibility) {
        const filters = [
          "any",
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
        ];
        this.setVesselFilter(filters);
      } else if (v.isMyVesselVisibility && !v.isOtherVesselVisibility) {
        const filters = ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS];
        this.setVesselFilter(filters);
      } else if (!v.isMyVesselVisibility && v.isOtherVesselVisibility) {
        const filters = [
          "==",
          "objectType",
          C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS,
        ];
        this.setVesselFilter(filters);
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_MARKER_LAYER,
          "visibility",
          "none",
        );
      } else {
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_HISTORY_TRACK_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_TEXT_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_POLYGON_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
          "visibility",
          "none",
        );
        this.map.setLayoutProperty(
          C.LAYER_ID.RG_VESSEL_MARKER_LAYER,
          "visibility",
          "none",
        );
      }

      // 不審船
      this.setFilterRgObjectLayer(v.isRgObjectVisibility);

      // プライベートプロット
      if (this.showPrivatePlot) {
        this.setFilterPrivatePlotLayer(v.isPrivatePlotVisibility);
      }
    },

    setVesselFilter(filters) {
      this.map.setFilter(C.LAYER_ID.RG_HISTORY_TRACK_LAYER, filters);
      this.map.setFilter(
        C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        filters,
      );
      this.map.setFilter(C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER, filters);
      this.map.setFilter(C.LAYER_ID.RG_VESSEL_TEXT_LAYER, filters);
      this.map.setFilter(C.LAYER_ID.RG_HISTORY_TRACK_LAYER, [
        "all",
        ["==", "visible", true],
        filters,
      ]);
      this.map.setFilter(C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER, [
        "all",
        ["==", "visible", true],
        filters,
      ]);
      this.map.setFilter(C.LAYER_ID.RG_VESSEL_POLYGON_LAYER, [
        "all",
        ["==", "hasSize", true],
        filters,
      ]);
      this.map.setFilter(C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER, [
        "all",
        ["==", "hasSize", false],
        filters,
      ]);
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_HISTORY_TRACK_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_VESSEL_TEXT_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_VESSEL_POLYGON_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
        "visibility",
        "visible",
      );
      this.map.setLayoutProperty(
        C.LAYER_ID.RG_VESSEL_MARKER_LAYER,
        "visibility",
        "visible",
      );
    },

    setLayerClickEvents() {
      let self = this;
      // 船舶レイヤーのクリックイベント
      let onClickVessel = (evt) => {
        //航跡の表示設定
        let props = evt.features[0].properties;
        let copyVisibleTracks = JSON.parse(JSON.stringify(self.visibleTracks));
        let vesselId = props.vesselId || props.id;
        if (this.isVisibleTrack(props.objectType, vesselId)) {
          const newVisibleTracks = copyVisibleTracks.vessels.filter(
            (v) => v !== vesselId,
          );
          copyVisibleTracks.vessels = newVisibleTracks;
          this.SET_VISIBLE_TRACKS(copyVisibleTracks);
        } else {
          copyVisibleTracks.vessels.push(vesselId);
          this.SET_VISIBLE_TRACKS(copyVisibleTracks);
        }
      };
      this.map.on("click", C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER, onClickVessel);
      this.map.on("click", C.LAYER_ID.RG_VESSEL_POLYGON_LAYER, onClickVessel);
      this.map.on(
        "click",
        C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
        onClickVessel,
      );
      // 不審船のクリックイベント
      const onClickRgObject = (evt) => {
        // 航跡の表示設定
        const props = evt.features[0].properties;
        const objectId = props.id;
        if (this.isVisibleTrack(props.objectType, objectId)) {
          this.deSelectRgObject(objectId);
        } else {
          const { geometry, type } = evt.features[0];
          const selectedRgObject = {
            id: objectId,
            geometry,
            properties: props,
            type,
          };

          // store更新のタイミングで詳細パネルの描画データがうまく反映されない可能性があるので、クリック時のデータを保持
          this.tmpSelectedRgObjects[objectId] = selectedRgObject;

          const payload = { toBeAddedId: objectId };
          this.addVisibleTracksRgObject(payload);
        }
      };
      this.map.on("click", C.LAYER_ID.RG_OBJECT_SYMBOL_LAYER, onClickRgObject);
    },

    setMousePointer() {
      let self = this;
      const keys = Object.keys(C.LAYER_ID);
      keys.forEach((key) => {
        if (
          C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER === C.LAYER_ID[key] ||
          C.LAYER_ID.RG_VESSEL_POLYGON_LAYER === C.LAYER_ID[key] ||
          C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER === C.LAYER_ID[key] ||
          C.LAYER_ID.RG_OBJECT_SYMBOL_LAYER === C.LAYER_ID[key]
        ) {
          this.map.on("mouseenter", C.LAYER_ID[key], () => {
            self.map.getCanvas().style.cursor = "pointer";
          });
          this.map.on("mouseleave", C.LAYER_ID[key], () => {
            self.map.getCanvas().style.cursor = "";
          });
        }
      });
    },

    setMapLayerPopup() {
      const {
        RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        RG_OBJECT_WAKE_HOVER_EVENT_LAYER,
      } = C.LAYER_ID;
      const mapCanvas = this.map.getCanvas();
      let popup = null;

      const createPopup = (properties) => {
        const { lat, lon, speed, epoch, name } = properties;

        const speedLabel = this.$t("Views.Vessel.Velocity");
        const dataTimeLabel = this.$t("Views.Commons.DataTime");
        const wakeLineLabel = this.$t("Views.Vessel.WakeLine");
        const latitudeLabel = this.$t("Views.Vessel.Latitude");
        const longitudeLabel = this.$t("Views.Vessel.Longitude");

        const roundedSpeed = (Math.round(speed * 10 ** 1) / 10 ** 1).toFixed(1);
        const dataTime = moment.unix(epoch).format("YYYY/MM/DD HH:mm:ss");
        const flooredLatitude = (Math.trunc(lat * 10 ** 8) / 10 ** 8).toFixed(
          8,
        );
        const flooredLongitude = (Math.trunc(lon * 10 ** 8) / 10 ** 8).toFixed(
          8,
        );

        return new Mapbox.Popup({
          className: `${C.RG_WAKE_LINE_POPUP_WRAPPER_CLASS_NAME}`,
          closeButton: false,
          closeOnClick: false,
        }).setHTML(
          `<strong>${name}</strong><span>の${wakeLineLabel}</span><br>
           <span class="mr-2">${speedLabel}: <strong>${roundedSpeed} kn</strong></span><br>
           <span>${dataTimeLabel}: <strong>${dataTime}</strong></span><br>
           <span>${latitudeLabel}: <strong>${flooredLatitude}</strong></span><br>
           <span>${longitudeLabel}: <strong>${flooredLongitude}</strong></span>
          `,
        );
      };
      const removePopup = () => {
        if (!popup) return;
        if (typeof popup.remove === "function") {
          popup.remove();
        }
      };
      /**
       * イベントに最も近いフィーチャーを計算します。
       * @param {Object} e - mapboxのイベントオブジェクト
       * @param {Array} layerIds - 計算対象のレイヤーIDの配列
       * @returns {Object} - 最も近いフィーチャーオブジェクト
       */
      const calculateClosestFeatureToEvent = (e, layerIds) => {
        const { point, lngLat } = e;
        const eventFeatures = this.map.queryRenderedFeatures(point, {
          layers: layerIds,
        });

        const pointFeature = turf.point([lngLat.lng, lngLat.lat]);
        const distances = eventFeatures.map((feature) => {
          return turf.pointToLineDistance(pointFeature, feature);
        });
        const closestDistance = Math.min(...distances);
        const closestDistanceIndex = distances.indexOf(closestDistance);

        return eventFeatures[closestDistanceIndex];
      };

      // ホバー時の処理を付与
      [
        RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        RG_OBJECT_WAKE_HOVER_EVENT_LAYER,
      ].forEach((layerId) => {
        this.map.on("mousemove", layerId, (e) => {
          if (e.defaultPrevented) return; // 同じイベントで複数回のcallbackが実行されるのを防ぐ
          e.preventDefault();

          removePopup();
          if (!this.isPlayBackStatusPause) return; // 再生中はポップアップを表示しない

          mapCanvas.style.cursor = "pointer";
          const feature = calculateClosestFeatureToEvent(e, [
            RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
            RG_OBJECT_WAKE_HOVER_EVENT_LAYER,
          ]);
          const { lng, lat } = e.lngLat;
          popup = createPopup(feature.properties);
          popup.setLngLat([lng, lat]).addTo(this.map);
        });
        this.map.on("mouseleave", layerId, () => {
          mapCanvas.style.cursor = "";
          removePopup();
        });
      });
    },

    addSourcePrivatePlotLayer() {
      Object.values(PrivatePlotMapSource).forEach((source) => {
        if (this.map.getSource(source.id)) return;
        this.map.addSource(source.id, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });
      });
    },

    addSourceRadarGridCameraMasterLayer() {
      if (this.map.getSource(C.SOURCE_ID.RG_CAMERA_MASTERS)) return;
      this.map.addSource(C.SOURCE_ID.RG_CAMERA_MASTERS, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    },

    addRadarGridCameraMasterLayer() {
      if (!this.map.getLayer(C.LAYER_ID.RG_CAMERA_MASTER_LAYER)) {
        this.map.addLayer({
          id: C.LAYER_ID.RG_CAMERA_MASTER_LAYER,
          type: "symbol",
          source: C.SOURCE_ID.RG_CAMERA_MASTERS,
          layout: {
            "icon-allow-overlap": true,
            "icon-pitch-alignment": "map",
            "icon-rotation-alignment": "map",
            "icon-image": "{icon}",
            "text-field": "{name}",
            "text-size": 12,
            "text-offset": [
              "case",
              ["boolean", ["get", "isTextTop"], true],
              ["literal", [0, -2]],
              ["literal", [0, 2]],
            ],
          },
          paint: {
            "text-color": "#FFFFFF",
            "text-halo-color": "#000000",
            "text-halo-width": 1,
          },
        });
      }
    },

    async setUpRadarGridCameraMasterLayer() {
      this.addSourceRadarGridCameraMasterLayer();
      this.addRadarGridCameraMasterLayer();
      this.updateRadarCameraMasterLayer();
    },

    updatePrivatePlotLayer() {
      this.updatePrivatePlotPointLayer();
      this.updatePrivatePlotPolygonLayer();
    },

    updateRadarCameraMasterLayer() {
      const source = this.map.getSource(C.SOURCE_ID.RG_CAMERA_MASTERS);
      if (source != null) {
        source.setData({
          type: "FeatureCollection",
          features: this.radarGridCameraMasterFeatures,
        });
      }
    },

    updatePrivatePlotPointLayer() {
      const source = this.map.getSource(
        PrivatePlotMapSource.PrivatePlotPointSource.id,
      );
      if (source != null) {
        source.setData({
          type: "FeatureCollection",
          features: this.privatePlotFeatures.filter(
            (f) => f.geometry.type === "Point",
          ),
        });
      }
    },

    updatePrivatePlotPolygonLayer() {
      const source = this.map.getSource(
        PrivatePlotMapSource.PrivatePlotPolygonSource.id,
      );
      if (source != null) {
        source.setData({
          type: "FeatureCollection",
          features: this.privatePlotFeatures.filter(
            (f) => f.geometry.type === "Polygon",
          ),
        });
      }
    },

    addPrivatePlotLayers() {
      if (!this.map.getLayer(PrivatePlotMapLayer.PrivatePlotTextLayer.id)) {
        this.map.addLayer({
          id: PrivatePlotMapLayer.PrivatePlotTextLayer.id,
          type: "symbol",
          source: PrivatePlotMapSource.PrivatePlotPointSource.id,
          minzoom: 8,
          maxzoom: 19,
          layout: {
            "text-field": "{title}",
            "text-anchor": "top",
            "text-size": 14,
          },
          paint: {
            "text-color": "#FFFFFF",
            "text-halo-color": "#000000",
            "text-halo-width": 1,
          },
        });
      }

      if (!this.map.getLayer(PrivatePlotMapLayer.PrivatePlotSymbolLayer.id)) {
        this.map.addLayer({
          id: PrivatePlotMapLayer.PrivatePlotSymbolLayer.id,
          type: "symbol",
          source: PrivatePlotMapSource.PrivatePlotPointSource.id,
          minzoom: 8,
          maxzoom: 19,
          layout: {
            "icon-ignore-placement": true,
            "icon-allow-overlap": true,
            "icon-optional": true,
            "icon-anchor": "bottom",
            "icon-image": "{icon}",
          },
          paint: {},
        });
      }

      if (!this.map.getLayer(PrivatePlotMapLayer.PrivatePlotPolygonLayer.id)) {
        this.map.addLayer({
          id: PrivatePlotMapLayer.PrivatePlotPolygonLayer.id,
          type: "fill",
          source: PrivatePlotMapSource.PrivatePlotPolygonSource.id,
          minzoom: 8,
          maxzoom: 19,
          paint: {
            "fill-color": {
              type: "identity",
              property: "color",
            },
            "fill-opacity": 0.7,
          },
        });
      }

      if (
        !this.map.getLayer(PrivatePlotMapLayer.PrivatePlotPolygonTextLayer.id)
      ) {
        this.map.addLayer({
          id: PrivatePlotMapLayer.PrivatePlotPolygonTextLayer.id,
          type: "symbol",
          source: PrivatePlotMapSource.PrivatePlotPolygonSource.id,
          minzoom: 8,
          maxzoom: 19,
          layout: {
            "text-field": "{title}",
            "text-anchor": "top",
            "text-size": 14,
          },
          paint: {
            "text-color": "#FFFFFF",
            "text-halo-color": "#000000",
            "text-halo-width": 1,
          },
        });
      }
    },

    addRgObjectLayers() {
      // レーダオブジェクト
      this.map.addLayer({
        id: C.LAYER_ID.RG_OBJECT_SYMBOL_LAYER,
        type: "symbol",
        minzoom: 5,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{icon}",
          "text-justify": "right",
          "icon-rotate": {
            type: "identity",
            property: "heading",
          },
          "text-field": "{trackNumber}",
          "text-size": this.rgFontSize,
          "text-offset": [3, 0],
          "text-optional": true,
          "text-font": this.rgFontText, // 14 or 18
        },
        paint: {
          "text-color": this.rgTextColor,
        },
        filter: ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
      });

      // 枠
      this.map.addLayer({
        id: C.LAYER_ID.RG_OBJECT_FLAME_LAYER,
        type: "symbol",
        minzoom: 5,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-image": "{frameIcon}",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
        filter: ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
      });

      // 航跡
      this.map.addLayer({
        id: C.LAYER_ID.RG_OBJECT_WAKE_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1],
        },
        filter: [
          "all",
          ["==", "visible", true],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
        ],
      });

      // 航跡のマウスイベント用レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_OBJECT_WAKE_HOVER_EVENT_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-width": 20.0,
          "line-opacity": 0.0,
        },
        filter: [
          "all",
          ["==", "visible", true],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.RG_OBJECT],
        ],
      });

      // 選択状態を示すサークル
      this.map.addLayer({
        id: C.LAYER_ID.RG_OBJECT_CIRCLE_LAYER,
        type: "circle",
        minzoom: 5,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        paint: {
          "circle-radius": 41, // Circle radius in pixels
          "circle-color": [
            "coalesce",
            ["get", "textColor"],
            C.RADAR_GRID_OBJECT_RISK_LEVEL[C.RG_RISK_LEVEL.CAUTION].color,
          ],
          "circle-opacity": 0.2, // Circle opacity
        },
        filter: this.rgCircleFilterCondition,
      });
    },

    setFilterPrivatePlotLayer(
      isShow, // Boolean
    ) {
      if (!this.map) return;
      Object.values(PrivatePlotMapLayer).forEach((layer) => {
        if (this.map.getLayer(layer.id)) {
          this.map.setFilter(layer.id, isShow);
        }
      });
    },

    setFilterRgObjectLayer(
      isShow, // Boolean
    ) {
      if (!this.map) return;
      [
        C.LAYER_ID.RG_OBJECT_SYMBOL_LAYER,
        C.LAYER_ID.RG_OBJECT_FLAME_LAYER,
        C.LAYER_ID.RG_OBJECT_WAKE_LAYER,
        C.LAYER_ID.RG_OBJECT_WAKE_HOVER_EVENT_LAYER,
        C.LAYER_ID.RG_OBJECT_CIRCLE_LAYER,
      ].forEach((layer) => {
        if (this.map.getLayer(layer)) {
          this.map.setLayoutProperty(
            layer,
            "visibility",
            isShow ? "visible" : "none",
          );
        }
      });
    },
    resetSelectedRgObjects() {
      this.selectedRgObjects = {};
    },
    resetTmpSelectedRgObjects() {
      this.tmpSelectedRgObjects = {};
    },
    deSelectRgObject(objectId) {
      // visibleTrackRgObjectsから削除
      this.removeVisibleTracksRgObject({ toBeRemovedId: objectId });
      // selectedRgObjectsから削除
      delete this.selectedRgObjects[objectId];
      delete this.tmpSelectedRgObjects[objectId];
    },
    deSelectAll() {
      // selectedRgObjectsと、visibleTrackRgObjectsを初期化
      this.resetVisibleTracksRgObject();
      this.resetSelectedRgObjects();
      this.resetTmpSelectedRgObjects();
    },
  },
  async created() {
    this.setRadarCameraData();
  },
  async mounted() {
    this.setUp();
  },
  async beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss" scoped>
.rg-object-history-map {
  width: 100vw;
  min-height: 100vh; /* Fallback */
  min-height: calc(var(--vh, 1vh) * 100);

  position: relative;
  ::v-deep .mapboxgl-ctrl-logo {
    display: none !important;
  }
  ::v-deep .rg-wake-line-popup-wrapper {
    z-index: 11; // 詳細パネルと重ならないようにする
  }
}
.rg-detail-info-area {
  display: contents;
}
</style>
